import { useEffect, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";
import { attachProp } from "../three/props3d";

// Clip order of every Meshy character export (see backend CLIPS).
export const CLIP_KEYS = ["idle", "walk", "slow_walk", "step", "wave", "celebrate", "reach", "sit", "stand_up", "jump"];
export const XR_PALETTE = { navy: "#0A2540", teal: "#17A2A2", gold: "#E0B33A" };

const draco = new DRACOLoader().setDecoderPath("/draco/");
const loader = new GLTFLoader().setDRACOLoader(draco);
const cache = {};
export const loadGlb = (url) => (cache[url] ||= loader.loadAsync(url));

/** Quest-optimised GLB (Draco + 1K WebP, exported by scripts/export_xr.sh), scaled to `height` metres, feet on the floor. Null if missing. */
export const useXRModel = (name, height, prop = null) => {
  const [m, setM] = useState(undefined);
  useEffect(() => {
    let live = true;
    (async () => {
      const g = await loadGlb(`/assets/xr/${name}.glb`);
      const scene = g.animations.length ? cloneSkinned(g.scene) : g.scene.clone(true);
      scene.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(scene, true), k = height / (box.max.y - box.min.y);
      scene.scale.setScalar(k); scene.position.y = -box.min.y * k;
      scene.traverse((o) => { if (o.isMesh) { o.frustumCulled = false; if (o.material) { o.material.envMapIntensity = 0.9; } } });
      const clips = Object.fromEntries(g.animations.map((a, i) => [CLIP_KEYS[i] || a.name, a]));
      g.animations.forEach((a) => { a.tracks = a.tracks.filter((t) => !/Hips\.position$/.test(t.name)); });
      if (prop) await attachProp(scene, prop.character, await loadGlb(`/assets/xr/${prop.file}.glb`), clips.idle).catch(() => {});
      const root = new THREE.Group(); root.add(scene);
      if (live) setM({ root, scene, clips });
    })().catch(() => live && setM(null));
    return () => { live = false; };
  }, [name, height, prop]);
  return m;
};
